// ═══════════════════════════════════════════════════════════════════════════
// INCOME TAB - UrbanQ v1.9.6
// Tabla de ingresos tipo Excel con CRUD
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw,
    FiTrendingUp, FiGrid, FiList, FiChevronUp, FiChevronDown
} from 'react-icons/fi';
import FinancialModal from './FinancialModal';

export default function IncomeTab({ year, month, token, apiUrl, scope, onNotification }) {
    const [loading, setLoading] = useState(true);
    const [incomes, setIncomes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [gridLines, setGridLines] = useState('grid-lines'); // 'grid-lines' | 'no-grid-lines' | 'horizontal-lines'
    const [zebra, setZebra] = useState(false);
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingIncome, setEditingIncome] = useState(null);

    // Inline editing
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');

    // Cargar categorias
    const fetchCategories = useCallback(async () => {
        try {
            const response = await fetch(`${apiUrl}/api/financial/categories?type=income&scope=${scope}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    }, [apiUrl, token, scope]);

    // Cargar ingresos
    const fetchIncomes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${apiUrl}/api/financial/income?year=${year}&month=${month}&scope=${scope}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setIncomes(result.data || []);
                setTotalAmount(result.totalAmount || 0);
            }
        } catch (err) {
            console.error('Error fetching incomes:', err);
            onNotification?.('Error al cargar ingresos', 'error');
        } finally {
            setLoading(false);
        }
    }, [year, month, token, apiUrl, scope, onNotification]);

    useEffect(() => {
        fetchCategories();
        fetchIncomes();
    }, [fetchCategories, fetchIncomes]);

    // Cargar preferencias de localStorage
    useEffect(() => {
        const savedGridLines = localStorage.getItem('finanzas_gridLines');
        const savedZebra = localStorage.getItem('finanzas_zebra');
        if (savedGridLines) setGridLines(savedGridLines);
        if (savedZebra) setZebra(savedZebra === 'true');
    }, []);

    // Guardar preferencias
    const toggleGridLines = () => {
        const options = ['grid-lines', 'no-grid-lines', 'horizontal-lines'];
        const currentIndex = options.indexOf(gridLines);
        const newValue = options[(currentIndex + 1) % options.length];
        setGridLines(newValue);
        localStorage.setItem('finanzas_gridLines', newValue);
    };

    const toggleZebra = () => {
        const newValue = !zebra;
        setZebra(newValue);
        localStorage.setItem('finanzas_zebra', String(newValue));
    };

    // Ordenamiento
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Filtrar y ordenar
    const filteredIncomes = incomes
        .filter(income => {
            const matchSearch = income.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                income.category_name?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchCategory = !filterCategory || income.category_id === parseInt(filterCategory);
            return matchSearch && matchCategory;
        })
        .sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            if (sortField === 'amount') {
                aVal = parseFloat(aVal);
                bVal = parseFloat(bVal);
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

    const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-EC', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // CRUD Operations
    const handleSave = async (data) => {
        try {
            const url = editingIncome
                ? `${apiUrl}/api/financial/income/${editingIncome.id}`
                : `${apiUrl}/api/financial/income`;

            const method = editingIncome ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ ...data, scope })
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.(editingIncome ? 'Ingreso actualizado' : 'Ingreso creado', 'success');
                fetchIncomes();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error saving income:', err);
            onNotification?.('Error al guardar', 'error');
            throw err;
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Estas seguro de eliminar este ingreso?')) return;

        try {
            const response = await fetch(`${apiUrl}/api/financial/income/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.('Ingreso eliminado', 'success');
                fetchIncomes();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error deleting income:', err);
            onNotification?.('Error al eliminar', 'error');
        }
    };

    // Inline editing
    const handleCellDoubleClick = (income, field) => {
        setEditingCell({ id: income.id, field });
        setEditValue(income[field] || '');
    };

    const handleCellBlur = async () => {
        if (!editingCell) return;

        const income = incomes.find(i => i.id === editingCell.id);
        if (!income || income[editingCell.field] === editValue) {
            setEditingCell(null);
            return;
        }

        try {
            await handleSave({
                ...income,
                [editingCell.field]: editingCell.field === 'amount' ? parseFloat(editValue) : editValue
            });
        } catch (err) {
            // Error ya manejado
        }
        setEditingCell(null);
    };

    const handleCellKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCellBlur();
        } else if (e.key === 'Escape') {
            setEditingCell(null);
        }
    };

    const openEditModal = (income) => {
        setEditingIncome(income);
        setModalOpen(true);
    };

    const openNewModal = () => {
        setEditingIncome(null);
        setModalOpen(true);
    };

    if (loading) {
        return (
            <div className="urbanq-finanzas-loading">
                <div className="urbanq-finanzas-spinner"></div>
            </div>
        );
    }

    const gridLinesLabel = {
        'grid-lines': 'Con lineas',
        'no-grid-lines': 'Sin lineas',
        'horizontal-lines': 'Solo horizontal'
    };

    return (
        <div className="urbanq-finanzas-income-tab">
            <div className="urbanq-finanzas-section">
                <div className="urbanq-finanzas-section-header">
                    <h2><FiTrendingUp /> Ingresos</h2>
                    <button className="urbanq-finanzas-btn primary" onClick={openNewModal}>
                        <FiPlus /> Nuevo Ingreso
                    </button>
                </div>

                {/* Toolbar */}
                <div className="urbanq-finanzas-toolbar">
                    <div className="urbanq-finanzas-toolbar-left">
                        <div className="urbanq-finanzas-search-box">
                            <FiSearch />
                            <input
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="urbanq-finanzas-filter-group">
                            <span className="urbanq-finanzas-filter-label">Categoria:</span>
                            <select
                                className="urbanq-finanzas-select"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <option value="">Todas</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="urbanq-finanzas-toolbar-right">
                        <button
                            className={`urbanq-finanzas-grid-toggle ${gridLines === 'grid-lines' ? 'active' : ''}`}
                            onClick={toggleGridLines}
                            title="Cambiar estilo de grilla"
                        >
                            <FiGrid /> {gridLinesLabel[gridLines]}
                        </button>

                        <button
                            className={`urbanq-finanzas-grid-toggle ${zebra ? 'active' : ''}`}
                            onClick={toggleZebra}
                            title="Alternar filas zebra"
                        >
                            <FiList /> Zebra
                        </button>

                        <button className="urbanq-finanzas-btn-icon" onClick={fetchIncomes}>
                            <FiRefreshCw />
                        </button>
                    </div>
                </div>

                {/* Barra de totales */}
                <div className="urbanq-finanzas-totals-bar">
                    <div className="urbanq-finanzas-totals-item">
                        <span className="urbanq-finanzas-totals-label">Total:</span>
                        <span className="urbanq-finanzas-totals-value positive">
                            {formatCurrency(totalAmount)}
                        </span>
                    </div>
                    <div className="urbanq-finanzas-totals-item">
                        <span className="urbanq-finanzas-totals-label">Registros:</span>
                        <span className="urbanq-finanzas-totals-value">
                            {filteredIncomes.length}
                        </span>
                    </div>
                </div>

                {/* Tabla */}
                <div className="urbanq-finanzas-table-wrapper">
                    {filteredIncomes.length > 0 ? (
                        <table className={`urbanq-finanzas-table ${gridLines} ${zebra ? 'zebra' : ''}`}>
                            <thead>
                                <tr>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort('date')}
                                    >
                                        Fecha
                                        {sortField === 'date' && (
                                            <span className="sort-icon">
                                                {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                                            </span>
                                        )}
                                    </th>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort('description')}
                                    >
                                        Descripcion
                                        {sortField === 'description' && (
                                            <span className="sort-icon">
                                                {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                                            </span>
                                        )}
                                    </th>
                                    <th>Categoria</th>
                                    <th
                                        className="sortable numeric"
                                        onClick={() => handleSort('amount')}
                                    >
                                        Monto
                                        {sortField === 'amount' && (
                                            <span className="sort-icon">
                                                {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                                            </span>
                                        )}
                                    </th>
                                    <th>Metodo</th>
                                    <th>Cuenta</th>
                                    <th style={{ width: '80px' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredIncomes.map(income => (
                                    <tr key={income.id}>
                                        <td className="date">{formatDate(income.date)}</td>

                                        {/* Descripcion editable */}
                                        <td
                                            className={editingCell?.id === income.id && editingCell?.field === 'description' ? 'editing' : ''}
                                            onDoubleClick={() => handleCellDoubleClick(income, 'description')}
                                        >
                                            {editingCell?.id === income.id && editingCell?.field === 'description' ? (
                                                <input
                                                    type="text"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onBlur={handleCellBlur}
                                                    onKeyDown={handleCellKeyDown}
                                                    autoFocus
                                                />
                                            ) : (
                                                income.description
                                            )}
                                        </td>

                                        <td>
                                            <span
                                                className="urbanq-finanzas-category-badge"
                                                style={{
                                                    background: `${income.category_color}20`,
                                                    color: income.category_color
                                                }}
                                            >
                                                {income.category_name}
                                            </span>
                                        </td>

                                        {/* Monto editable */}
                                        <td
                                            className={`numeric amount positive ${editingCell?.id === income.id && editingCell?.field === 'amount' ? 'editing' : ''}`}
                                            onDoubleClick={() => handleCellDoubleClick(income, 'amount')}
                                        >
                                            {editingCell?.id === income.id && editingCell?.field === 'amount' ? (
                                                <input
                                                    type="number"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onBlur={handleCellBlur}
                                                    onKeyDown={handleCellKeyDown}
                                                    autoFocus
                                                    step="0.01"
                                                />
                                            ) : (
                                                formatCurrency(income.amount)
                                            )}
                                        </td>

                                        <td>{income.payment_method}</td>
                                        <td>{income.bank_account || '-'}</td>
                                        <td>
                                            <div className="urbanq-finanzas-row-actions">
                                                <button
                                                    className="urbanq-finanzas-row-action"
                                                    onClick={() => openEditModal(income)}
                                                    title="Editar"
                                                >
                                                    <FiEdit2 />
                                                </button>
                                                <button
                                                    className="urbanq-finanzas-row-action delete"
                                                    onClick={() => handleDelete(income.id)}
                                                    title="Eliminar"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="urbanq-finanzas-empty">
                            <FiTrendingUp className="urbanq-finanzas-empty-icon" />
                            <h3>No hay ingresos registrados</h3>
                            <p>Comienza agregando tu primer ingreso</p>
                            <button className="urbanq-finanzas-btn primary" onClick={openNewModal}>
                                <FiPlus /> Agregar Ingreso
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            <FinancialModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditingIncome(null);
                }}
                onSave={handleSave}
                type="income"
                editData={editingIncome}
                categories={categories}
            />
        </div>
    );
}
